import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGT } from 'gt-react';
import type { GetPricelistResponse } from '@erp/rpc';
import { orpc } from '@/api/rpc/client';
import { waitForBackgroundJob } from '@/api/background-jobs/waitForBackgroundJob';
import { useToast } from 'src/context/ToastContext';
import { triggerDownload } from 'src/lib/pricelist-utils';

/** Invalidate every view that shows pricelist assignments or extractions. */
export function useInvalidatePricelistViews() {
  const queryClient = useQueryClient();
  return function invalidate() {
    const keys = [
      orpc.core.companies.listCompanies.key(),
      orpc.core.companies.listCompanyPricelists.key(),
      orpc.inventoryManagement.pricelist.listUnassigned.key(),
      orpc.inventoryManagement.pricelist.listAll.key(),
      orpc.inventoryManagement.pricelist.get.key(),
      orpc.inventoryManagement.pricelist.listEvents.key(),
      orpc.inventoryManagement.pricelist.listVersions.key(),
    ];
    for (const queryKey of keys) {
      void queryClient.invalidateQueries({ queryKey });
    }
  };
}

/**
 * Restore a pricelist's tables to a previous version. Rejects on failure so the
 * caller's confirmation can stay open, having already reported the error.
 */
export function useRestorePricelistVersion() {
  const gt = useGT();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const invalidatePricelistViews = useInvalidatePricelistViews();

  return useMutation({
    ...orpc.inventoryManagement.pricelist.restoreVersion.mutationOptions(),
    onSuccess: (result) => {
      invalidatePricelistViews();
      void queryClient.invalidateQueries({
        queryKey: orpc.inventoryManagement.pricelist.getExtractedCsv.key(),
      });
      showToast({
        title: gt('Restored', {
          context: 'toast title when a pricelist version is restored',
        }),
        message: gt('The pricelist was restored to version {version}.', {
          context: 'toast message when a pricelist version is restored',
          version: String(result.versionNumber),
        }),
        color: 'success',
      });
    },
    onError: () => {
      showToast({
        title: gt('Error', { context: 'toast title for error state' }),
        message: gt('Failed to restore that version.', {
          context: 'toast message when restoring a pricelist version fails',
        }),
        color: 'error',
      });
    },
  });
}

/**
 * Delete a pricelist. The record is soft-deleted server-side, so this only
 * removes it from the pricelist views — `onDeleted` navigates away from the
 * detail page it was just deleted from.
 */
export function useDeletePricelist(onDeleted: () => void) {
  const gt = useGT();
  const { showToast } = useToast();
  const invalidatePricelistViews = useInvalidatePricelistViews();

  return useMutation({
    ...orpc.inventoryManagement.pricelist.delete.mutationOptions(),
    onSuccess: () => {
      invalidatePricelistViews();
      showToast({
        title: gt('Pricelist deleted', {
          context: 'toast title when a pricelist is deleted',
        }),
        message: gt('The pricelist was removed.', {
          context: 'toast message when a pricelist is deleted',
        }),
        color: 'success',
      });
      onDeleted();
    },
    onError: () => {
      showToast({
        title: gt('Error', { context: 'toast title for error state' }),
        message: gt('Failed to delete that pricelist.', {
          context: 'toast message when deleting a pricelist fails',
        }),
        color: 'error',
      });
    },
  });
}

/**
 * The per-pricelist actions: download the uploaded source file, download the
 * extracted CSV, and re-run the extraction. Failures surface as toasts.
 */
export function usePricelistActions(pricelist: GetPricelistResponse) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const invalidatePricelistViews = useInvalidatePricelistViews();
  const [loadingFile, setLoadingFile] = useState(false);
  const [loadingCsv, setLoadingCsv] = useState(false);
  const [isWaitingForExtraction, setIsWaitingForExtraction] = useState(false);

  const reExtractMutation = useMutation(
    orpc.inventoryManagement.pricelist.retryExtraction.mutationOptions(),
  );

  const csvDoc = pricelist.csvDocuments[0];

  function showError(message: string) {
    showToast({
      title: gt('Error', { context: 'toast title for error state' }),
      message,
      color: 'error',
    });
  }

  // The loading flag is cleared on both paths rather than in a `finally`: the
  // React Compiler doesn't support try/finally.
  async function downloadFile() {
    setLoadingFile(true);
    try {
      const result = await queryClient.fetchQuery(
        orpc.inventoryManagement.pricelist.getDocumentUrl.queryOptions({
          input: { documentId: pricelist.id },
        }),
      );
      window.open(result.url, '_blank');
      setLoadingFile(false);
    } catch {
      setLoadingFile(false);
      showError(
        gt('Failed to download file.', {
          context: 'toast message when source file download fails',
        }),
      );
    }
  }

  async function downloadCsv() {
    if (!csvDoc) return;
    setLoadingCsv(true);
    try {
      // The stored blob IS the download — no reason to page a large pricelist
      // through the tables API and reassemble it. A signed URL streams it
      // straight from storage at any size.
      const result = await queryClient.fetchQuery(
        orpc.inventoryManagement.pricelist.getDocumentUrl.queryOptions({
          input: { documentId: csvDoc.id },
        }),
      );
      try {
        // Fetch and re-wrap so the saved file keeps the pricelist's name
        // rather than the blob's storage key…
        const response = await fetch(result.url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        triggerDownload(await response.blob(), csvDoc.name);
      } catch {
        // …but if storage CORS refuses the read, the signed URL still
        // downloads fine opened directly.
        window.open(result.url, '_blank');
      }
      setLoadingCsv(false);
    } catch {
      setLoadingCsv(false);
      showError(
        gt('Failed to download CSV.', {
          context: 'toast message when CSV download fails',
        }),
      );
    }
  }

  async function reExtract() {
    setIsWaitingForExtraction(true);
    try {
      const { backgroundJobId } = await reExtractMutation.mutateAsync({
        pdfDocumentId: pricelist.id,
      });
      const extraction = await waitForBackgroundJob(backgroundJobId);
      if (extraction.outcome === 'failed') {
        setIsWaitingForExtraction(false);
        showError(
          extraction.errorMessage ??
            gt('Failed to re-extract CSV.', {
              context: 'toast message when CSV re-extraction fails',
            }),
        );
        return;
      }
      if (extraction.outcome === 'vanished') {
        setIsWaitingForExtraction(false);
        showError(
          gt('Stopped tracking this extraction. Check the pricelist.', {
            context:
              'toast message when a re-extraction job can no longer be read',
          }),
        );
        return;
      }
      invalidatePricelistViews();
      void queryClient.invalidateQueries({
        queryKey: orpc.inventoryManagement.pricelist.getExtractedCsv.key(),
      });
      setIsWaitingForExtraction(false);
    } catch (error) {
      setIsWaitingForExtraction(false);
      showError(
        error instanceof Error && error.message
          ? error.message
          : gt('Failed to re-extract CSV.', {
              context: 'toast message when CSV re-extraction fails',
            }),
      );
    }
  }

  return {
    csvDoc,
    loadingFile,
    loadingCsv,
    isReExtracting: reExtractMutation.isPending || isWaitingForExtraction,
    downloadFile,
    downloadCsv,
    reExtract,
    invalidatePricelistViews,
  };
}
